// state/screen-flow.ts — V28 Phase F3.
//
// Read-only navigation metadata. Ports the prototype FLOW object
// (`docs/specs/v28-prototype/index.html` line 3371) into a typed map keyed by a
// normalized "flow key", then resolves the active screen from
// `useLocation().pathname`. This replaces the prototype's `data-has-back/next/chat`
// attributes — `AppFrame` reads `{ back, next, chat }` to wire gestures + edge
// zones + the swipe-up chat hint.
//
// IMPORTANT: this layer is read-only nav metadata. It does NOT change any
// persisted identifier — routes, localStorage keys, and session modes stay
// exactly as the real router (App.tsx) declares them. `back`/`next` values are
// real route PATHS (with `:param` placeholders); `AppFrame` substitutes the live
// params before navigating.
//
// Prototype id → real route mapping (blueprint F3, lines 125-135):
//   prof / prof-chat        → /prof                         ({} — chat is native here)
//   progressi-argomento     → /studia/argomento/:argomentoId (back: /progresso, chat)
//   lesson                  → /studia/lezione/fast/:microTopicId
//                             + /studia/lezione/deep/:argomentoId (back+next: argomento, chat)
//   rivedi                  → /studia/rivedi/:…   (back: argomento, chat) — route lands in S5
//   ripasso                 → /studia/ripasso/:…  (back+next: argomento, chat) — route lands in S5
//   appendice-detail        → /appendice/:argomentoId/:principleId (back: appendice, next: sibling, chat)
//   simula phases           → SimulaScreen intra-screen machine (no inter-route flow)
//   messaggi/profilo/appendice index → {}

import { useLocation } from 'react-router-dom';
import { useNavOverride, type NavActions } from './nav-override';

export interface ScreenFlow {
  /** Real route path to navigate to on swipe-right / left-edge tap. */
  back?: string;
  /** Real route path to navigate to on swipe-left / right-edge tap. */
  next?: string;
  /** When true, the swipe-up chat sheet + SwipeHint are available on this screen. */
  chat?: boolean;
  /** Imperative nav actions a screen registered via `nav-override`. When an
   *  `onBack` / `onNext` is present it takes precedence over the route-based
   *  back/next in AppFrame, AND makes the corresponding edge zone appear even
   *  when this entry has no static `back`/`next` route (e.g. Simula, whose
   *  prev/next-question moves are registered dynamically by SimulaScreen). */
  navActions?: NavActions;
}

// The flow keys are stable, normalized identifiers — NOT routes themselves.
export type FlowKey =
  | 'prof'
  | 'progressi'
  | 'progressi-argomento'
  | 'lesson-fast'
  | 'lesson-deep'
  | 'lesson-guidata'
  | 'punti-chiave'
  | 'rivedi'
  | 'ripasso'
  | 'appendice'
  | 'appendice-signs'
  | 'appendice-theory'
  | 'appendice-domande'
  | 'appendice-detail'
  | 'simula'
  | 'messaggi'
  | 'profilo'
  | 'unknown';

// Typed FLOW config. `back`/`next` are real route paths with `:param` tokens that
// AppFrame resolves against the current location's params before navigating.
export const FLOW: Record<FlowKey, ScreenFlow> = {
  prof:                 {},
  // HUB page (Progressi cockpit) — NO chat sheet (founder chat-scoping contract,
  // Item B 2026-06-11): the Prof chat is available only INSIDE a specific lesson
  // or a specific sub-argomento in Appendice, never on a hub/index page.
  progressi:            {},
  // Progressi drill-down (the argomento sotto-temi list). This is a DRILL-DOWN
  // hub, not "a specific lesson", so per the founder's enumeration it has NO chat
  // — the chat becomes available once you ENTER one of its lessons. `chat`
  // omitted (falsy). (Was `chat: true` — that surfaced the chat one level too
  // early and fell through to the GLOBAL 'chiedi' thread, the reported bug.)
  'progressi-argomento': {},
  // A single lesson. Edge controls are registered by the mounted lesson screen
  // only after it knows the current chapter/micro-topic position. This keeps
  // navigation inside the lesson and prevents a first-chapter back arrow/action.
  'lesson-fast':        { chat: true },
  'lesson-deep':        { chat: true },
  // FP5 guided lesson. The screen registers imperative onNext/onBack (chapter
  // paging) via nav-override. No static back fallback: first chapter has no
  // left edge arrow/action, and later back stays within the current lesson.
  'lesson-guidata':     { chat: true },
  'punti-chiave':        { back: '/studia/argomento/:argomentoId', chat: true },
  rivedi:               { back: '/studia/argomento/:argomentoId', chat: true },
  ripasso:              { back: '/studia/argomento/:argomentoId', next: '/studia/argomento/:argomentoId', chat: true },
  appendice:            {},
  // FP6 Segnali sign index — gesture-back returns to the Appendice index.
  'appendice-signs':    { back: '/appendice', chat: true },
  // R2-2: the per-argomento THEORY chapter — gesture-back returns to the
  // argomento theory list (/appendice/:argomentoId); in-page chat available.
  'appendice-theory':   { back: '/appendice/:argomentoId', chat: true },
  // FP6 official-question browser — gesture-back to the argomento theory list.
  'appendice-domande':  { back: '/appendice/:argomentoId', chat: true },
  // back to the index; "next" → next sibling principle. The sibling principleId
  // is async KB data, so it can't live in a static template here — the detail
  // screen registers the concrete next-sibling route via `nav-override`, which
  // `useScreenFlow` merges over this entry's (absent) static `next`. `chat` on.
  // Founder feedback 2026-07-14 (#3): back from a sotto-argomento returns to
  // ITS argomento page (the sotto-argomenti list), not the Appendice index.
  'appendice-detail':   { back: '/appendice/:argomentoId', chat: true },
  // Simula owns an intra-screen phase machine — no inter-route FLOW here. The
  // running phase registers imperative onBack/onNext (prev/next QUESTION) via
  // the nav-actions registry; AppFrame + EdgeNavZones become active in Simula
  // because a registered navAction makes the edge zones appear (registry-aware,
  // not a placeholder route — picker/postmortem/review register nothing).
  simula:               {},
  messaggi:             {},
  profilo:              {},
  unknown:              {},
};

/**
 * Normalize a pathname to a FlowKey. Order matters: more-specific prefixes are
 * tested before their parents so `/studia/argomento/x` doesn't fall through to a
 * looser match. Legacy aliases (`/chiedi`, `/`) collapse onto their real screen.
 */
export function flowKeyForPath(pathname: string): FlowKey {
  // Strip any trailing slash for stable prefix matching (keep root as '/').
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  // /prof/messaggio-vocale is now a stub-redirect to /prof (the voice-message
  // recorder was removed in the V28xFP merge); it collapses onto 'prof'.
  if (p === '/' || p === '/prof' || p === '/chiedi' || p.startsWith('/prof/messaggio-vocale')) return 'prof';
  if (p.startsWith('/studia/lezione/fast')) return 'lesson-fast';
  if (p.startsWith('/studia/lezione/deep')) return 'lesson-deep';
  if (p.startsWith('/studia/lezione/guidata')) return 'lesson-guidata';
  if (p.startsWith('/studia/punti-chiave')) return 'punti-chiave';
  if (p.startsWith('/studia/rivedi')) return 'punti-chiave';
  if (p.startsWith('/studia/ripasso')) return 'lesson-fast';
  if (p.startsWith('/studia/argomento')) return 'progressi-argomento';
  if (p === '/progresso') return 'progressi';
  // FP6 Segnali sign index — the static `segnali` segment (incl. /segnali/:id)
  // must be tested BEFORE the two-segment detail regex so /appendice/segnali/:id
  // doesn't collapse onto 'appendice-detail' (wrong back + async sibling-next).
  if (p.startsWith('/appendice/segnali')) return 'appendice-signs';
  // Theory chapter = appendice + argomento + the static `teoria` segment.
  // Tested BEFORE the generic two-segment detail rule below so it doesn't
  // collapse onto 'appendice-detail'.
  if (/^\/appendice\/[^/]+\/teoria(?:\/|$)/.test(p)) return 'appendice-theory';
  // FP6 official-question browser — static `domande` segment, also before detail.
  if (/^\/appendice\/[^/]+\/domande(?:\/|$)/.test(p)) return 'appendice-domande';
  // Detail = appendice + two path segments after it; index = the rest.
  if (/^\/appendice\/[^/]+\/[^/]+/.test(p)) return 'appendice-detail';
  if (p.startsWith('/appendice')) return 'appendice';
  if (p.startsWith('/simula')) return 'simula';
  if (p.startsWith('/messaggi')) return 'messaggi';
  if (p.startsWith('/profilo')) return 'profilo';
  return 'unknown';
}

/**
 * Read the active screen's flow metadata from the current location. Returns the
 * normalized key alongside `{ back, next, chat }` so callers can both branch on
 * the key and wire navigation.
 */
export function useScreenFlow(): ScreenFlow & { key: FlowKey } {
  const { pathname } = useLocation();
  const { nextOverride, navActions } = useNavOverride();
  const key = flowKeyForPath(pathname);
  const flow = FLOW[key];
  // A screen-registered dynamic `next` ROUTE (e.g. the Appendice detail's async
  // next sibling) overrides the static FLOW.next when present. Everything else
  // (back / chat) stays from the static map. The imperative `navActions`
  // (onBack/onNext callbacks — Simula questions, Fast lesson micro-topics) are
  // surfaced so AppFrame can prefer them over routes AND EdgeNavZones can show
  // even when the static entry has no back/next route.
  return {
    key,
    ...flow,
    ...(nextOverride ? { next: nextOverride } : {}),
    navActions,
  };
}
